import React, { useEffect, useState } from "react";
import Layout from "../../components/web/Layout";
import Carousel from "../../components/ui/carousel/Carousel";
import useCarousel from "../../hooks/ui/useCarousel";
import { FaMinus, FaPlus } from "react-icons/fa";
import { formatToINR, getFileUrl } from "../../utils/helper";
import Container from "../../components/ui/container/Container";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { BRAND_NAME } from "../../utils/constant";
import apiRequest from "../../utils/apiRequest";

const Product = () => {
  const [activeTab, setActiveTab] = useState("description");
  const { sliderRef, slideToIndex } = useCarousel();
  const responsive = [
    { breakpoint: 1024, settings: { slidesToShow: 1 } },
    { breakpoint: 768, settings: { slidesToShow: 1 } },
    { breakpoint: 480, settings: { slidesToShow: 1 } },
  ];
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [product, setProducts] = useState(null);
  const { id } = useParams();
  useEffect(() => {
    setIsLoading(true);
    apiRequest
      .getProductById({ id: id })
      .then((data) => {
        setProducts(data.data.data.product);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);
  
  const increaseQuantity = () => setQuantity(quantity + 1);

  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  return (
    <Layout>
      <Helmet>
        <title>
          {product?.name ? product?.name : ""} - {BRAND_NAME}
        </title>
      </Helmet>
      {!isLoading && product ? (
        <>
          <Container>
            <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
              <div className="grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-4">
                <div className="flex flex-row order-2 gap-3 lg:order-1 lg:flex-col">
                  {product?.images.slice(0, 5).map((item, index) => {
                    return (
                      <div
                        onClick={() => slideToIndex(index)}
                        key={index}
                        className="h-auto w-36 lg:w-auto rounded-[10px] overflow-hidden cursor-pointer"
                      >
                        <img
                          className="h-[100%] w-[100%] object-cover"
                          src={getFileUrl(item.image)}
                          alt="product"
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="order-1 lg:order-2 w-[100%] overflow-hidden">
                  <Carousel
                    sliderRef={sliderRef}
                    slideToShow={1}
                    fade={true}
                    responsiveConfig={responsive}
                    infinite={false}
                  >
                    {product?.images.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className="bg-white relative h-[300px] md:h-[450px] rounded-[10px] overflow-hidden"
                        >
                          <div
                            style={{
                              height: "100%",
                              width: "100%",
                              backgroundImage: `url(${getFileUrl(item.image)})`,
                              backgroundAttachment: "fixed",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          ></div>
                        </div>
                      );
                    })}
                  </Carousel>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-black flex sm:items-center items-start sm:flex-row flex-col gap-3 text-xl font-[600]">
                    <h2>{product?.name}</h2>
                    <span
                      className={`bg-green-100 text-green-600 px-2 py-1 text-[12px] sm:text-[10px] font-[400] sm:font-[500]`}
                    >
                      In Stock
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-primaryDark text-xl font-[500] my-2">
                    {formatToINR(product?.mrp)}
                  </p>
                  <p className="text-gray-500 text-sm line-through font-[500] my-2">
                    {formatToINR(product?.finalPrice)}
                  </p>
                  {product?.offPercent && (
                    <p className="p-1 text-[12px] sm:text-[10px] font-[400] sm:font-[500] text-red-500 bg-red-100">
                      {product?.offPercent}% OFF
                    </p>
                  )}
                </div>
                <div className="my-4 border-b"></div>
                <ul className="ml-6 mt-4 space-y-1 sm:text-sm text-[12px] text-gray-500 list-disc">
                  {product?.bulletPoints.map((item, index) => {
                    return <li key={index}>{item}</li>;
                  })}
                </ul>
                <p className="sm:text-sm text-[12px] text-gray-400 mt-4">
                  {product?.shortDescription}
                </p>
                <div className="my-4 border-b"></div>
                <div className="w-full max-w-lg mx-auto mt-4 text-nowrap">
                  <div className="flex flex-col items-center gap-3 xxs:flex-row">
                    {/* Quantity Control Section */}
                    <div className="flex items-center p-1 bg-white border rounded-full">
                      <button
                        onClick={decreaseQuantity}
                        className="p-2 text-black bg-gray-200 rounded-full hover:bg-gray-300"
                      >
                        <FaMinus />
                      </button>

                      <span className="w-12 font-semibold text-center text-md md:text-lg">
                        {quantity}
                      </span>

                      <button
                        onClick={increaseQuantity}
                        className="p-2 text-black bg-gray-200 rounded-full hover:bg-gray-300"
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <button className="flex-1 w-full py-3 ml-4 font-medium text-center text-white rounded-full bg-primaryDark hover:bg-primaryDark">
                      Check Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Container>
          <div className="flex flex-col justify-center overflow-x-auto border-b border-gray-300 md:items-center md:flex-row">
            <div className="flex overflow-x-auto text-nowrap whitespace-nowrap no-scrollbar">
              <button
                className={`sm:px-6 px-3 py-2 text-md md:text-lg font-medium ${activeTab === "description"
                    ? "text-primaryDark border-b-2 border-primaryDark"
                    : "text-gray-600 hover:text-primaryDark"
                  }`}
                onClick={() => setActiveTab("description")}
              >
                Description
              </button>

              <button
                className={`sm:px-6 px-3 py-2 text-md md:text-lg font-medium ${activeTab === "additional-info"
                    ? "text-primaryDark border-b-2 border-primaryDark"
                    : "text-gray-600 hover:text-primaryDark"
                  }`}
                onClick={() => setActiveTab("additional-info")}
              >
                Additional Information
              </button>
            </div>
          </div>

          <Container>
            {activeTab === "description" && (
              <div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="sm:text-sm text-[12px] text-gray-400">
                    {product?.description}
                  </p>
                  <ul className="ml-6 mt-4 space-y-1 sm:text-sm text-[12px] text-gray-500 list-disc">
                    {product.bulletPoints.map((item, index) => {
                      return <li key={index}>{item}</li>;
                    })}
                  </ul>
                  <p className="sm:text-sm text-[12px] text-gray-400 mt-4">
                    {product?.shortDescription}
                  </p>
                </div>
              </div>
            )}

            {activeTab === "additional-info" && (
              <div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="sm:text-sm text-[12px] text-left text-gray-600 bg-gray-100">
                        <th className="px-4 py-2 border-b">Attribute</th>
                        <th className="px-4 py-2 border-b">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(product.keyValue).map(
                        ([key, value], i) => (
                          <tr key={key}>
                            <td className="px-4 py-2 sm:text-sm text-[12px] text-gray-700 border-b">
                              {key}
                            </td>
                            <td className="px-4 py-2 sm:text-sm text-[12px] text-gray-700 border-b">
                              {value}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Container>
        </>
      ) : (
        <Shimmer />
      )}
    </Layout>
  );
};
export default Product;

const Shimmer = () => {
  return (
    <Container>
      <div className="animate-pulse">
        <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
          {/* Left side image shimmer */}
          <div className="grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-4">
            <div className="flex flex-row order-2 gap-3 lg:order-1 lg:flex-col">
              {Array(5)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="h-[80px] w-[100px] bg-gray-200 rounded-[10px]"
                  ></div>
                ))}
            </div>
            <div className="order-1 lg:order-2 w-full h-[300px] md:h-[450px] bg-gray-200 rounded-[10px]" />
          </div>

          {/* Right side details shimmer */}
          <div className="p-4 bg-white">
            <div className="w-3/4 h-6 mb-4 bg-gray-200 rounded"></div>
            <div className="w-1/2 h-5 mb-2 bg-gray-200 rounded"></div>
            <div className="w-1/4 h-5 mb-4 bg-gray-200 rounded"></div>
            <div className="my-4 border-b"></div>
            <div className="space-y-2">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="w-full h-4 bg-gray-200 rounded" />
                ))}
            </div>
            <div className="my-4 border-b"></div>
            <div className="flex gap-4 mt-4">
              <div className="w-24 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1 h-10 bg-gray-300 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
